import { Component, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MaterialModule } from '../../material/material.module';
import { getDateRange, getEndOfWeek, getStartOfWeek } from '../../utils/utils';
import { PipesModule } from '../../pipes/pipes.module';
import { animate, style, transition, trigger } from '@angular/animations';
import { OverlayRef } from '@angular/cdk/overlay';
import { MatCalendar } from '@angular/material/datepicker';
import { CalendarInterval } from '../../enums/dateIntervalCalendar.enum';
import { ICalendarRangeData } from '../../models/calendarRangeData';
import { isNullOrUndefined } from '@pineapplelab/util';

@Component({
	selector: 'app-calendar-chooser',
	standalone: true,
	imports: [
		MaterialModule,
		PipesModule
	],
	templateUrl: './calendar-chooser.component.html',
	styleUrl: './calendar-chooser.component.scss',
	animations: [
		trigger('animation', [
			transition(':enter', [
				style({
					opacity: 0,
					scale: 0.8
				}),
				animate('0.10s ease-in-out',
					style({
						opacity: 1,
						scale: 1
					})
				)
			]),
			transition(':leave', [
				style({
					opacity: 1,
					scale: 1
				}),
				animate('0.10s ease-in-out',
					style({
						opacity: 0,
						scale: 0.8
					})
				)
			])
		])
	]
})
export class CalendarChooserComponent implements OnInit {

	@ViewChild('fromCalendar', { static: true }) fromCalendar: MatCalendar<any>;
	@ViewChild('toCalendar', { static: true }) toCalendar: MatCalendar<any>;

	@HostBinding('@animation') animationState = true;

	today = new Date();
	private _readableRange: string = null;
	private _rangeStringRelation: { [key in CalendarInterval]: string } = {
		4: 'general.filter',
		1: 'manage-students.this_month',
		0: 'manage-students.this_week',
		3: 'manage-students.this_year',
		2: 'manage-students.this_trimester'
	};
	Interval = CalendarInterval;

	@Input() from: Date = new Date();
	@Input() to: Date;
	@Input() containerStyles: { [key: string]: any } = {};
	@Input() overlay: OverlayRef;
	@Input() applyDefaultRange: boolean = true;

	@Output() onIntervalSelected = new EventEmitter<ICalendarRangeData>();
	@Output() onClose = new EventEmitter<boolean>();

	ngOnInit(): void {
		if (this.applyDefaultRange) {
			// default range is the current week
			this.setRange(CalendarInterval.WEEK);
		}
	}

	setRange(interval: CalendarInterval): void {
		switch (interval) {
			case CalendarInterval.MONTH:
				this.from = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
				this.to = new Date(this.today.getFullYear(), this.today.getMonth() + 1, 0);
				break;
			case CalendarInterval.WEEK:
				this.from = getStartOfWeek(this.today);
				this.to = getEndOfWeek(this.today);
				break;
			case CalendarInterval.YEAR:
				this.from = new Date(this.today.getFullYear(), 0, 1);
				this.to = new Date(this.today.getFullYear(), 11, 31);
				break;
			case CalendarInterval.TRIMESTER:
				const currentQuarter = Math.floor(this.today.getMonth() / 3);
				this.from = new Date(this.today.getFullYear(), currentQuarter * 3, 1);
				this.to = new Date(this.today.getFullYear(), currentQuarter * 3 + 3, 0);
				break;
			default:
				break;
		}
		this._readableRange = this._rangeStringRelation[interval] ? this._rangeStringRelation[interval] : null;
		this.fromCalendar.activeDate = this.from;
		this.toCalendar.activeDate = this.to;
	}

	applyRange(): void {
		const calendarRangeData: ICalendarRangeData = { from: this.from, to: this.to, applyTranslation: false, readableRange: this._readableRange };
		const dateRange = getDateRange(this.from, this.to);

		if (isNullOrUndefined(dateRange)) {
			calendarRangeData.applyTranslation = false;
			calendarRangeData.readableRange = `${this.from.toLocaleDateString(navigator.language)} → ${this.to.toLocaleDateString(navigator.language)}`
		} else {
			calendarRangeData.applyTranslation = true;
			calendarRangeData.readableRange = this._rangeStringRelation[dateRange];
		}
		this.onIntervalSelected.emit(calendarRangeData);
	}

	clean(): void {
		this.onIntervalSelected.emit(null);
	}

}
